import { ArrowLeft, Cookie, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const CookiePolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const thirdPartyCookies = [
    { name: "Google Ads (Google Adwords)", url: "https://www.google.com/intl/hu/policies/privacy" },
    { name: "Facebook (Meta)", url: "https://www.facebook.com/help/cookies/" },
    { name: "Instagram", url: "https://help.instagram.com/402411646841720" },
    { name: "TikTok Pixel", url: "https://www.tiktok.com/legal/page/us/privacy-policy/en" },
    { name: "Google Analytics", url: "https://www.google.com/intl/hu/policies/privacy" },
    { name: "Google Tag Manager", url: "https://www.google.com/intl/hu/policies/privacy" },
    { name: "ActiveCampaign", url: "https://www.activecampaign.com/legal/privacy-policy" },
    { name: "Microsoft Clarity", url: "https://clarity.microsoft.com/privacy" },
    { name: "GoHighLevel", url: "https://www.gohighlevel.com/privacy-policy" },
  ];

  const browserLinks = [
    { name: "Google Chrome", url: "https://support.google.com/chrome/answer/95647" },
    { name: "Mozilla Firefox", url: "https://support.mozilla.org/hu/kb/sutik-engedelyezese-es-tiltasa" },
    { name: "Microsoft Internet Explorer", url: "https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-7-x-8-x-9-x-10-x-11-x-4976777d-2979-7fa7-3a99-1831f8506f5e" },
    { name: "Microsoft Edge", url: "https://support.microsoft.com/en-us/microsoft-edge/view-cookies-in-microsoft-edge-a7d95376-f2cd-8e4a-25dc-1de753474879" },
    { name: "Safari (Mac)", url: "https://support.apple.com/hu-hu/guide/safari/sfri11471/mac" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Bar */}
      <div className="bg-slate-900 text-white py-4">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl flex items-center justify-between">
          <Link
            to="/"
            data-testid="cookie-back-link"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Cookie className="w-4 h-4" />
            Cookie szabályzat
          </div>
        </div>
      </div>

      {/* Title Section */}
      <div className="bg-blue-900 py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Cookie className="w-4 h-4" />
            COOKIE SZABÁLYZAT
          </div>
          <h1
            className="text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="cookie-title"
          >
            Cookie (Süti) Szabályzat
          </h1>
          <p className="text-blue-200 text-base md:text-lg">
            TURPA-BUILD KFT. — Hatálybalépés dátuma: 2025. szeptember 1.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 max-w-4xl py-12 md:py-16" data-testid="cookie-content">
        <article className="prose prose-slate max-w-none" style={{ fontFamily: "'DM Sans', sans-serif" }}>

          {/* Intro */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 mb-12 border border-slate-100 space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
            <p>
              Jelen szabályzat a <strong>valyog-mester.hu</strong> weboldalon (üzemeltető: TURPA-BUILD KFT.) alkalmazott sütikre vonatkozik.
            </p>
            <p>
              A weboldal használatával Ön elfogadja a jelen szabályzat rendelkezéseit. Amennyiben nem ért egyet a feltételekkel, kérjük, ne használja a weboldalt.
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              1. Mik azok a sütik (cookie-k)?
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
              A cookie-k kisméretű adatfájlok, amelyeket a weboldal küld a böngészőnek. Segítik a weboldal működését, javítják a felhasználói élményt, és lehetővé teszik a felhasználói viselkedés elemzését, valamint a releváns hirdetések megjelenítését.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              2. Hogyan működik a cookie-k használata?
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p>
                A rendszer egy egyedi azonosítót hoz létre, amelyet a böngésző eltárol. Visszatéréskor a böngésző elküldi ezt az azonosítót, így működik például:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Bejelentkezési állapot megjegyzése</li>
                <li>Űrlapértékek tárolása</li>
                <li>Konverziók mérése</li>
                <li>Remarketing-hirdetések</li>
                <li>Személyre szabott tartalom</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              3. A weboldalon használt sütik céljai
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
              A valyog-mester.hu sütiket használ a weboldal működéséhez, felhasználói élmény javításához, statisztikai mérésekhez, teljesítményméréshez, hirdetésekhez, kampánykövetéshez és GoHighLevel CRM-folyamatokhoz.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              4. A sütik típusai
            </h2>

            {/* 4.1 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                4.1 Alapműködést biztosító sütik
              </h3>
              <ul className="list-disc list-inside space-y-2 pl-2 text-slate-600 text-sm md:text-base">
                <li>Session sütik</li>
                <li>Értesítési üzenetek állapota</li>
                <li>Bejelentkezési adatok</li>
                <li>Sütibeállítások</li>
                <li>Anonimizált Analytics sütik</li>
              </ul>
            </div>

            {/* 4.2 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                4.2 Statisztikai és teljesítménymérő sütik
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Google Analytics, Google Optimize — a felhasználói viselkedést elemzik, személyes azonosítás nélkül.
              </p>
            </div>

            {/* 4.3 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                4.3 Célzó- és hirdetési sütik
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {thirdPartyCookies.map((item) => (
                  <a
                    key={item.name}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors group"
                  >
                    <span className="font-medium text-slate-700 text-sm">{item.name}</span>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0 ml-2" />
                  </a>
                ))}
              </div>
            </div>

            {/* 4.4 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                4.4 GoHighLevel sütik
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Kampány- és funnelkövetés, űrlapadatok, automatizált e-mail/SMS indítás.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              5. A sütik kezelése és kikapcsolása
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed">
              A felhasználó a böngészőjében bármikor módosíthatja vagy törölheti a sütiket. A tiltás csökkentheti a weboldal működését.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              6. Böngészők süti beállításai
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
              A legnépszerűbb böngészők süti beállításairól az alábbi linkeken tájékozódhat:
            </p>
            <div className="space-y-3">
              {browserLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-slate-50 rounded-xl p-4 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors group"
                >
                  <span className="font-medium text-slate-700 text-sm md:text-base">{item.name}</span>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0 ml-2" />
                </a>
              ))}
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              7. Jogszabályi háttér
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>2003. évi C. törvény</li>
                <li>2001. évi CVIII. törvény</li>
                <li>2011. évi CXII. törvény</li>
                <li>2002/58/EK irányelv</li>
                <li>GDPR (EU 2016/679 rendelet)</li>
              </ul>
              <p>
                A jelen szabályzat a 2003. évi C. törvény, a 2001. évi CVIII. törvény, a 2011. évi CXII. törvény, a 2002/58/EK irányelv és a GDPR (EU 2016/679 rendelet) rendelkezései alapján készült.
              </p>
            </div>
          </section>

        </article>

        {/* Back to home */}
        <div className="text-center pt-8 border-t border-slate-200">
          <Link
            to="/"
            data-testid="cookie-back-link-bottom"
            className="inline-flex items-center gap-2 bg-blue-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>
        </div>
      </div>

      {/* Mini Footer */}
      <div className="bg-slate-900 text-slate-500 text-center py-6 text-sm">
        © {new Date().getFullYear()} Vályog-Mester / TURPA-BUILD KFT. Minden jog fenntartva.
      </div>
    </div>
  );
};

export default CookiePolicy;
